import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Copy, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api`;

export const MANUAL_SEND_NOTE = 'This link is shown only once and is NOT sent automatically. Copy it now '
  + 'and send it yourself from the operator mailbox to this person only.';

const STATUS_LABEL = {
  pending: 'Pending', redeemed: 'Activated', revoked: 'Revoked', expired: 'Expired',
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

/**
 * PILOT-ACCESS-003A: the platform super-admin's invitations for ONE Company.
 *
 * Issues an invitation (email + role, `user` by default; `admin` only by explicit choice) and
 * shows the activation link ONCE — built here from the page origin and the server's one-time
 * path, kept only in this component's memory, never stored or logged. Lists the Company's
 * invitations with their status (no token material ever comes back) and revokes or reissues;
 * a reissue invalidates the old link and shows the new one once.
 */
const CompanyInvitations = ({ company, token, onClose }) => {
  const [invitations, setInvitations] = useState(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('user');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [oneTimeLink, setOneTimeLink] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const listUrl = `${API_URL}/admin/companies/${encodeURIComponent(company.id)}/invitations`;

  const load = useCallback(async () => {
    try {
      const response = await axios.get(listUrl, { headers: { Authorization: `Bearer ${token}` } });
      setInvitations(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setInvitations([]);
      setError(err.response?.data?.detail || 'Invitations could not be loaded');
    }
  }, [listUrl, token]);

  useEffect(() => {
    setOneTimeLink(null);
    setError('');
    load();
  }, [load]);

  const showOnce = (data) => {
    setOneTimeLink({
      url: `${window.location.origin}${data.activation_path}`,
      email: data.invitation.email,
      role: data.invitation.role,
    });
  };

  const fail = (err, fallback) => {
    const detail = err.response?.data?.detail;
    setError(typeof detail === 'string' ? detail : fallback);
  };

  const handleIssue = async (event) => {
    event.preventDefault();
    if (busy) return;
    setError('');
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    setBusy(true);
    try {
      const response = await axios.post(listUrl, { email, role }, auth);
      showOnce(response.data);
      setEmail('');
      setRole('user');
      await load();
    } catch (err) {
      fail(err, 'The invitation could not be created');
    } finally {
      setBusy(false);
    }
  };

  const act = async (invitation, action) => {
    if (busy) return;
    setError('');
    setBusy(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/invitations/${encodeURIComponent(invitation.id)}/${action}`, {}, auth,
      );
      if (action === 'reissue') showOnce(response.data);
      else toast.success('Invitation revoked');
      await load();
    } catch (err) {
      fail(err, `The invitation could not be ${action === 'reissue' ? 'reissued' : 'revoked'}`);
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(oneTimeLink.url);
      toast.success('Link copied');
    } catch (err) {
      toast.error('Copy failed: select the link and copy it manually');
    }
  };

  return (
    <div className="world-panel p-4 space-y-4" data-testid={`company-invitations-${company.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-white font-medium">Invite member — {company.name}</p>
          <p className="text-xs text-slate-400">
            The email must belong to {company.primary_domain}. Joining requires this invitation; the
            domain alone grants nothing.
          </p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onClose} aria-label="Close invitations"
          data-testid="company-invitations-close">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <form onSubmit={handleIssue} className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-end"
        data-testid="invite-form">
        <div className="space-y-1">
          <Label htmlFor="invite-member-email" className="text-sm text-slate-300">Email</Label>
          <Input id="invite-member-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder={`name@${company.primary_domain}`} className="world-input" data-testid="invite-email" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="invite-member-role" className="text-sm text-slate-300">Role</Label>
          <select id="invite-member-role" value={role} onChange={(e) => setRole(e.target.value)}
            className="world-input h-10 rounded-md px-3 text-sm" data-testid="invite-role">
            <option value="user">Sales (user)</option>
            <option value="admin">Company admin</option>
          </select>
        </div>
        <Button type="submit" disabled={busy} className="world-btn-primary" data-testid="invite-create-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create invitation'}
        </Button>
      </form>
      {error && <p className="text-sm text-red-400" role="alert" data-testid="invite-error">{error}</p>}

      {oneTimeLink && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 space-y-2" data-testid="invite-one-time">
          <p className="text-sm text-amber-200">
            Activation link for {oneTimeLink.email} ({oneTimeLink.role === 'admin' ? 'Company admin' : 'Sales'}).
          </p>
          <div className="flex gap-2">
            <Input readOnly value={oneTimeLink.url} className="world-input font-mono text-xs"
              onFocus={(e) => e.target.select()} data-testid="invite-one-time-url" />
            <Button type="button" variant="outline" size="sm" onClick={copyLink} data-testid="invite-copy">
              <Copy className="h-4 w-4 mr-1" /> Copy
            </Button>
          </div>
          <p className="text-xs text-amber-200/80">{MANUAL_SEND_NOTE}</p>
          <Button type="button" variant="ghost" size="sm" onClick={() => setOneTimeLink(null)}
            data-testid="invite-one-time-dismiss">
            I have copied it — hide the link
          </Button>
        </div>
      )}

      {invitations && invitations.length === 0 && (
        <p className="text-sm text-slate-400" data-testid="invite-empty">No invitations for this company.</p>
      )}
      {invitations && invitations.length > 0 && (
        <table className="w-full text-sm" data-testid="invite-table">
          <thead>
            <tr className="text-left text-slate-400 border-b border-white/10">
              <th className="py-2 pr-4 font-medium">Email</th>
              <th className="py-2 pr-4 font-medium">Role</th>
              <th className="py-2 pr-4 font-medium">Status</th>
              <th className="py-2 pr-4 font-medium">Expires</th>
              <th className="py-2 font-medium" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {invitations.map((invitation) => (
              <tr key={invitation.id} className="border-b border-white/5" data-testid={`invite-row-${invitation.id}`}>
                <td className="py-2 pr-4 text-white">{invitation.email}</td>
                <td className="py-2 pr-4 text-slate-300">{invitation.role === 'admin' ? 'Company admin' : 'Sales'}</td>
                <td className="py-2 pr-4 text-slate-300">{STATUS_LABEL[invitation.status] || invitation.status}</td>
                <td className="py-2 pr-4 text-slate-400">{formatDate(invitation.expires_at)}</td>
                <td className="py-2 text-right space-x-2 whitespace-nowrap">
                  {invitation.status === 'pending' && (
                    <Button type="button" variant="outline" size="sm" disabled={busy}
                      onClick={() => act(invitation, 'revoke')} data-testid={`invite-revoke-${invitation.id}`}>
                      Revoke
                    </Button>
                  )}
                  {invitation.status !== 'redeemed' && (
                    <Button type="button" variant="outline" size="sm" disabled={busy}
                      onClick={() => act(invitation, 'reissue')} data-testid={`invite-reissue-${invitation.id}`}>
                      Reissue
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default CompanyInvitations;
